import { useCallback, useMemo } from 'react'
import type { JSX } from 'react'
import { Outlet } from 'react-router-dom'

import type { ErrorLike } from '@apollo/client'

import { PageHeader, PageLayout } from '@buffer-mono/popcorn'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { range } from '~publish/helpers/range'
import { useInMemoryRestoreScroll } from '~publish/hooks/scroll/useRestoreScroll'
import { usePageTitle } from '~publish/hooks/usePageTitle'

import { EmptyFeedList } from './EmptyFeedList'
import {
  useFeedsNavigation,
  useParsedFeedsData,
} from '../../../components/FeedsNavigation/hooks'
import { mostRecentRefresh } from '../../helpers/mostRecentRefresh'
import { useFeedItems } from '../../hooks/useFeedItems'
import type { FeedItem } from '../../hooks/useFeedItems'
import { useRefreshAllFeeds } from '../../hooks/useRefreshAllFeeds'
import {
  FeedArticleCardSkeleton,
  FeedArticleLoadFailure,
} from '../FeedArticleList/FeedArticleCard'
import { FeedArticleList } from '../FeedArticleList/FeedArticleList'
import { ListHeaderTitle } from '../ListHeader/ListHeaderTitle'

import styles from './AllFeedsList.module.css'

type LoadState =
  | { state: 'loading' }
  | { state: 'error'; error: ErrorLike }
  | { state: 'success'; items: FeedItem[] }

export const AllFeedsList = (): JSX.Element => {
  const organizationId = useOrganizationId()
  const pageLayoutScrollableRef = useInMemoryRestoreScroll('all-feeds')

  usePageTitle('All Feeds')

  /**
   * Use centralized parsed feeds data hook
   */
  const {
    feeds,
    collections,
    existingFeeds,
    totalFeeds,
    loading: loadingSidebarData,
  } = useParsedFeedsData(organizationId ?? undefined)

  /**
   * Extract feed and collection IDs for feed items query
   */
  const { rootFeedIds, collectionIds } = useMemo(() => {
    const computedRootFeedIds = feeds.map((feed) => feed.id)
    const computedCollectionIds = collections.map((collection) => collection.id)

    return {
      rootFeedIds: computedRootFeedIds,
      collectionIds: computedCollectionIds,
    }
  }, [feeds, collections])

  /**
   * Use centralized feeds navigation hook for tier, limits, and actions
   */
  const {
    tier,
    limitReached,
    handleCreateFeed,
    handleDeleteFeed,
    handleUpgradeClick,
  } = useFeedsNavigation({
    collectionsCount: collections.length,
    totalFeeds,
  })

  /**
   * Check if we should show empty state
   * Show empty state when there are no individual feeds, regardless of whether
   * collections exist (collections might be empty)
   */
  const hasNoFeeds = rootFeedIds.length === 0 && collectionIds.length === 0
  const shouldShowEmptyState = totalFeeds === 0

  const {
    loading: loadingFeedItems,
    items,
    fetchingMore,
    lastElementRef,
    error: feedItemsError,
  } = useFeedItems({
    organizationId,
    feedIds: rootFeedIds,
    feedCollectionIds: collectionIds,
  })

  const loadState: LoadState = useMemo(() => {
    if (feedItemsError) {
      return { state: 'error', error: feedItemsError }
    }

    if (items !== undefined && items !== null) {
      return { state: 'success', items }
    }

    if (loadingFeedItems) {
      return { state: 'loading' }
    }

    return { state: 'loading' }
  }, [feedItemsError, items, loadingFeedItems])

  const lastUpdated = useMemo(() => {
    const allFeeds = [
      ...feeds,
      ...collections.flatMap((collection) => collection.feeds),
    ]
    return mostRecentRefresh(allFeeds.map((feed) => feed.lastRefreshed))
  }, [feeds, collections])

  const refreshAllFeeds = useRefreshAllFeeds()
  const handleRefresh = useCallback(async (): Promise<{
    success: boolean
    message: string
  }> => {
    if (!organizationId) {
      return {
        success: false,
        message: 'Organization not found',
      }
    }

    try {
      const { success } = await refreshAllFeeds({
        organizationId,
        feedIds: rootFeedIds,
        collectionIds,
      })
      if (!success) {
        return {
          success: false,
          message: 'Failed to refresh feeds',
        }
      }
      return {
        success: true,
        message: 'Your feeds have been refreshed!',
      }
    } catch {
      return {
        success: false,
        message: 'Failed to refresh feeds',
      }
    }
  }, [organizationId, refreshAllFeeds, rootFeedIds, collectionIds])

  return (
    <>
      <Outlet />
      <PageLayout className={styles.pageLayout} ref={pageLayoutScrollableRef}>
        {!hasNoFeeds && (
          <PageHeader separator={false} className={styles.narrowHeader}>
            <PageHeader.Row size="narrow">
              <ListHeaderTitle
                title="All Feeds"
                isCollection={false}
                isLoading={loadingSidebarData}
                lastUpdated={lastUpdated}
                onRefresh={handleRefresh}
              />
            </PageHeader.Row>
          </PageHeader>
        )}

        <PageLayout.Container size="narrow">
          {loadState.state === 'error' && <FeedArticleLoadFailure />}

          {loadState.state === 'loading' && !hasNoFeeds && (
            <div aria-live="polite" aria-label="Loading feed items">
              {range(20).map((index) => (
                <FeedArticleCardSkeleton key={index} />
              ))}
            </div>
          )}

          {loadState.state === 'success' &&
            loadState.items.length === 0 &&
            shouldShowEmptyState && (
              <EmptyFeedList
                existingFeeds={existingFeeds}
                tier={tier}
                limitReached={limitReached}
                onAddFeed={handleCreateFeed}
                onRemoveFeed={handleDeleteFeed}
                onUpgrade={handleUpgradeClick}
              />
            )}

          {loadState.state === 'success' && loadState.items.length > 0 && (
            <FeedArticleList
              items={loadState.items}
              fetchingMore={fetchingMore}
              lastElementRef={lastElementRef}
            />
          )}
        </PageLayout.Container>
      </PageLayout>
    </>
  )
}
